import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Star, Users, Play } from 'lucide-react';
import { useCourse } from '../contexts/CourseContext';
import { useCurrency } from '../contexts/CurrencyContext';

const Courses = () => {
  const { courses, loading, getCategories } = useCourse();
  const { convertPrice, formatPrice } = useCurrency();
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');

  // Obtenir les catégories depuis le contexte
  const categories = getCategories();

  // Filtrer les cours selon la catégorie et la recherche
  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'Tous' || course.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des cours...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-text mb-4">
            Découvrez nos cours
          </h1>
          <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Des centaines de cours de qualité pour développer vos compétences 
            et accélérer votre carrière
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un cours..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Résultats de recherche */}
        {searchQuery && (
          <div className="text-center mb-6">
            <p className="text-gray-600">
              {filteredCourses.length} cours trouvé{filteredCourses.length > 1 ? 's' : ''} pour "{searchQuery}"
            </p>
          </div>
        )}

        {/* Grille des cours */}
        {filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredCourses.map((course) => (
              <div key={course.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                {/* Thumbnail */}
                <div className="relative">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-primary text-white px-2 py-1 rounded-full text-xs font-medium">
                    {course.category}
                  </div>
                  <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      course.level === 'Débutant' ? 'bg-green-100 text-green-800' :
                      course.level === 'Intermédiaire' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {course.level}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-text mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-medium">{course.rating}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{course.students}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{course.duration}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Par {course.instructor}
                    </div>
                  </div>

                  {/* Prix */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl sm:text-2xl font-bold text-primary">
                        {formatPrice(convertPrice(course.price, 'EUR'))}
                      </span>
                      {course.originalPrice > course.price && (
                        <span className="text-gray-500 line-through">
                          {formatPrice(convertPrice(course.originalPrice, 'EUR'))}
                        </span>
                      )}
                    </div>
                    <Link
                      to={`/course/${course.id}`}
                      className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 text-sm font-medium"
                    >
                      Voir le cours
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun cours trouvé</h3>
            <p className="text-gray-600">
              {searchQuery 
                ? `Aucun cours ne correspond à votre recherche "${searchQuery}"`
                : `Aucun cours disponible dans la catégorie "${selectedCategory}"`
              }
            </p>
            <button
              onClick={() => {
                setSelectedCategory('Tous');
                setSearchQuery('');
              }}
              className="mt-4 text-primary hover:text-primary/80 font-medium"
            >
              Voir tous les cours
            </button>
          </div>
        )}

        {/* Pagination */}
        {filteredCourses.length > 0 && (
          <div className="flex justify-center mt-12">
            <nav className="flex items-center gap-2">
              <button className="px-3 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50">
                Précédent
              </button>
              <button className="px-3 py-2 bg-primary text-white rounded-lg">1</button>
              <button className="px-3 py-2 text-gray-700 hover:text-primary">2</button>
              <button className="px-3 py-2 text-gray-700 hover:text-primary">3</button>
              <button className="px-3 py-2 text-gray-500 hover:text-gray-700">
                Suivant
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;
